// Buscador de la pestaña «aforo»: filtra las funciones por título de la obra y por fecha,
// siempre en hora de España. El filtrado es local: GET /admin/funciones ya devuelve todas
// (≈1600 en Stage) con su título.
import { inicioDiaMadrid, partesMadrid } from '../../utils/hora';

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

/** Minúsculas, sin tildes ni espacios repetidos: «Óscar  y LA Ñ» → «oscar y la n». */
export const normalizar = texto => String(texto ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

const anioCompleto = y => y === undefined ? undefined : (y.length === 2 ? 2000 + Number(y) : Number(y));
const valida = ({ day, month }) => (day === undefined || (day >= 1 && day <= 31)) && month >= 1 && month <= 12;

/**
 * Separa de la búsqueda la parte de fecha y el resto (texto del título). Acepta «27/09»,
 * «27-09-2026», «27/09/26», «septiembre», «septiembre 2026», «27 de septiembre de 2026» o un año
 * suelto («2026»). Devuelve `fecha: null` si no hay ninguna fecha reconocible.
 */
export function interpretarBusqueda(consulta) {
  let resto = normalizar(consulta); const fecha = {};
  const numerica = resto.match(/(?:^|\s)(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{4}|\d{2}))?(?=\s|$)/);
  const candidata = numerica && { day: Number(numerica[1]), month: Number(numerica[2]), year: anioCompleto(numerica[3]) };
  if (candidata && valida(candidata)) { Object.assign(fecha, candidata); resto = resto.replace(numerica[0], ' '); }
  else {
    const nombre = resto.match(new RegExp(`(?:^|\\s)(?:(\\d{1,2}) (?:de )?)?(${MESES.join('|')})(?: (?:de |del )?(\\d{4}))?(?=\\s|$)`));
    const porNombre = nombre && { day: nombre[1] === undefined ? undefined : Number(nombre[1]), month: MESES.indexOf(nombre[2]) + 1, year: anioCompleto(nombre[3]) };
    if (porNombre && valida(porNombre)) { Object.assign(fecha, porNombre); resto = resto.replace(nombre[0], ' '); }
  }
  if (fecha.year === undefined) {
    const anio = resto.match(/(?:^|\s)((?:19|20)\d{2})(?=\s|$)/);
    if (anio) { fecha.year = Number(anio[1]); resto = resto.replace(anio[0], ' '); }
  }
  Object.keys(fecha).forEach(k => fecha[k] === undefined && delete fecha[k]);
  resto = normalizar(resto).replace(/^(?:de|del)(?: |$)|(?:^| )(?:de|del)$/g, '').trim();
  return { texto: normalizar(consulta), fecha: Object.keys(fecha).length ? fecha : null, resto };
}

const coincideFecha = (partes, fecha) => Object.keys(fecha).every(k => partes[k] === fecha[k]);

/** Prepara una sola vez el título normalizado y las partes de la fecha en Madrid de cada función. */
export const indexarFunciones = functions => functions.map(f => ({ f, titulo: normalizar(f.titulo), partes: partesMadrid(f.fecha) }));

/**
 * Filtra conservando el orden recibido. `periodo`: «todas», «proximas» (desde las 00:00 de hoy
 * en Madrid, así las de hoy siguen a mano) o «pasadas». Una función coincide si su título
 * contiene la búsqueda completa, o si su fecha cumple la parte de fecha y su título contiene el
 * resto: «1984» encuentra la obra «1984» y las funciones de ese año; «hamlet 27/09», solo esa.
 */
export function filtrarFunciones(indice, consulta = '', periodo = 'todas', ahora = Date.now()) {
  const { texto, fecha, resto } = interpretarBusqueda(consulta); const hoy = inicioDiaMadrid(ahora);
  return indice.filter(({ f, titulo, partes }) => {
    if (periodo === 'proximas' && f.fecha < hoy) return false;
    if (periodo === 'pasadas' && f.fecha >= hoy) return false;
    if (!texto || titulo.includes(texto)) return true;
    return !!fecha && coincideFecha(partes, fecha) && titulo.includes(resto);
  }).map(({ f }) => f);
}
